import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { Mail, Loader2, CheckCircle } from 'lucide-react';

/**
 * "Forgot PIN" — emails a one-time reset link. Endpoint always returns 200
 * regardless of whether the email exists, so we don't leak account presence.
 */
export default function ForgotPinPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await api.post('/auth/pin/forgot', { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 flex items-center justify-center px-6 py-12" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/docsite-logo.png" alt="DocSite" className="w-24 h-24 object-contain mx-auto mb-4" />
          <h1 className="text-2xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Reset your PIN</h1>
          <p className="text-sm text-slate-500 mt-1">
            We'll email you a one-time link to set a new 6-digit PIN.
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-pin-sent">
              <CheckCircle size={32} className="text-emerald-500 mx-auto mb-3" />
              <p className="text-sm text-slate-700 font-medium">Check your inbox.</p>
              <p className="text-xs text-slate-500 mt-2">If <span className="font-mono">{email}</span> is on file, a reset link is on its way. The link expires in 1 hour.</p>
              <Link to="/login" className="inline-block mt-6 text-sm text-sky-600 font-medium hover:text-sky-700" data-testid="forgot-pin-back">Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && <div className="text-xs text-red-600 bg-red-50 p-2 rounded-lg" data-testid="forgot-pin-error">{error}</div>}
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Your email</label>
                <input type="email" required autoFocus
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                  value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                  data-testid="forgot-pin-email" />
              </div>
              <button type="submit" disabled={loading}
                className="w-full rounded-full py-3 bg-sky-600 text-white font-medium hover:bg-sky-700 transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="forgot-pin-submit">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                {loading ? 'Sending…' : 'Email me a reset link'}
              </button>
              <Link to="/login" className="block text-center text-xs text-slate-500 hover:text-slate-700">Back to sign in</Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
